// src/pages/landing-builder/QuizFunnelBuilder.jsx
// ── Quiz Funnel builder ───────────────────────────────────────────────────────
// Single-page quiz: user answers all questions → CTA unlocks.
// Based exactly on the working old file.
import { useState } from 'react';
import { Button, Input } from '../../components/ui';
import {
  Palette, Type, HelpCircle, MousePointerClick, Bell, Plus, Trash2,
} from 'lucide-react';
import { ColorPicker } from './shared';

// ==================== QUIZ FUNNEL PREVIEW ====================
export const QuizFunnelPreview = ({ config, theme, previewTab = 'page', onSetPreviewTab }) => {
  const [answers, setAnswers] = useState({});
  const quiz = config.quiz || {};
  const primaryColor = theme?.primaryColor || '#008751';
  const questions = quiz.questions || [{ label: 'Question 1', options: ['Option A', 'Option B'] }];
  const selectedCount = Object.keys(answers).length;
  const totalQuestions = questions.length;
  const allAnswered = totalQuestions > 0 && selectedCount === totalQuestions;

  const selectAnswer = (qIdx, oIdx) => {
    setAnswers(prev => ({ ...prev, [qIdx]: oIdx }));
  };

  const tabs = [
    { id: 'page', label: 'Page' },
    ...(quiz.popupEnabled ? [{ id: 'popup', label: 'Popup' }] : []),
  ];

  return (
    <div
      className="min-h-full relative"
      style={{ backgroundColor: quiz.backgroundColor || '#7cb342', fontFamily: theme?.fontFamily || 'Segoe UI, sans-serif' }}
    >
      {/* Preview tab bar */}
      {tabs.length > 1 && (
        <div className="sticky top-0 z-10 flex gap-1 bg-card/95 border-b border-border/60 px-3 py-2 shadow-sm">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => onSetPreviewTab?.(tab.id)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                previewTab === tab.id
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
      <div className="max-w-md mx-auto py-8 px-4">
        <div className="rounded-lg shadow-lg p-6" style={{ backgroundColor: quiz.containerBgColor || '#ffffff' }}>

          {/* Logo */}
          {quiz.logoUrl && (
            <img src={quiz.logoUrl} alt="Logo" className="max-w-[200px] h-auto mx-auto mb-4" />
          )}

          {/* Title */}
          <h1
            className="text-center font-bold mb-2"
            style={{ color: quiz.titleColor || primaryColor, fontSize: quiz.titleSize || '20px' }}
          >
            {quiz.title || 'Answer to Continue'}
          </h1>

          {/* Subtitle */}
          {quiz.subtitle && (
            <p className="text-center mb-6 text-sm" style={{ color: quiz.subtitleColor || '#666' }}>
              {quiz.subtitle}
            </p>
          )}

          {/* Questions */}
          <div className="space-y-6">
            {questions.map((q, qIdx) => (
              <div key={qIdx} className="space-y-2">
                {q.label && (
                  <p className="font-medium text-sm" style={{ color: quiz.questionColor || '#333' }}>
                    {q.label}
                  </p>
                )}
                <div className={`grid gap-2 ${q.layout === 'horizontal' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {(q.options || ['Option A', 'Option B']).map((opt, oIdx) => (
                    <button
                      key={oIdx}
                      onClick={() => selectAnswer(qIdx, oIdx)}
                      className={`p-3 text-sm font-medium rounded-lg border-2 transition-all hover:scale-[1.01] text-left`}
                      style={{
                        borderColor: answers[qIdx] === oIdx ? primaryColor : '#e5e7eb',
                        backgroundColor: answers[qIdx] === oIdx ? `${primaryColor}15` : 'white',
                        color: quiz.optionTextColor || '#333',
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Progress bar */}
          {quiz.showProgress && totalQuestions > 0 && (
            <div className="mt-6 mb-4">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>{selectedCount} of {totalQuestions} answered</span>
                <span>{Math.round((selectedCount / totalQuestions) * 100)}%</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full transition-all duration-300 rounded-full"
                  style={{ width: `${(selectedCount / totalQuestions) * 100}%`, backgroundColor: primaryColor }}
                />
              </div>
            </div>
          )}

          {/* CTA — greyed out until all answered */}
          <button
            className={`w-full p-4 mt-6 text-lg font-bold rounded-lg transition-all relative ${allAnswered ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}`}
            style={{ backgroundColor: primaryColor, color: quiz.ctaTextColor || '#ffffff' }}
            disabled={!allAnswered}
          >
            {quiz.ctaText || 'Continue'}
            {quiz.isTestCta && (
              <span className="absolute -top-2 -right-2 bg-purple-500 text-white text-xs px-1.5 py-0.5 rounded-full">A/B</span>
            )}
          </button>

          {!allAnswered && quiz.ctaLockedMessage && (
            <p className="text-center text-xs text-muted-foreground mt-2">
              {quiz.ctaLockedMessage}
            </p>
          )}
        </div>
      </div>

      {/* Social proof */}
      {quiz.socialProofEnabled && (
        <div className="absolute bottom-20 left-4 bg-popover border border-border p-3 rounded-lg shadow-lg max-w-[280px] text-sm text-foreground">
          ✅ <strong>{quiz.socialProofNames?.[0] || 'John'} from {quiz.socialProofLocations?.[0] || 'Lagos'}</strong>{' '}
          {quiz.socialProofMessage || 'just completed'}
        </div>
      )}

      {/* Footer T&C */}
      {quiz.showTermsLink && previewTab !== 'popup' && (
        <div className="absolute bottom-0 left-0 right-0 text-center py-3 text-sm">
          <span style={{ color: quiz.termsColor || '#888' }}>
            {quiz.termsText || 'Subject to our'}{' '}
            <span className="underline cursor-pointer">
              {quiz.termsLinkText || 'T&Cs'}
            </span>
          </span>
        </div>
      )}

      {/* Popup tab — clean view */}
      {previewTab === 'popup' && quiz.popupEnabled && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
          <div className="bg-white p-6 rounded-xl shadow-2xl max-w-[360px] w-full mx-4 text-center">
            {quiz.popupTitle && (
              <h3 className="text-xl font-bold mb-3" style={{ color: '#dc2626' }}>
                {quiz.popupTitle}
              </h3>
            )}
            {quiz.popupMessage && (
              <p className="text-sm text-gray-600 mb-5">{quiz.popupMessage}</p>
            )}
            <button
              className="w-full p-3 rounded-lg font-semibold text-white relative"
              style={{ backgroundColor: primaryColor }}
            >
              {quiz.popupCtaText || 'Continue'}
              {quiz.popupIsTestCta && (
                <span className="absolute -top-2 -right-2 bg-purple-500 text-white text-xs px-1.5 py-0.5 rounded-full">A/B</span>
              )}
            </button>
            <p className="text-xs text-gray-400 mt-3">Popup preview</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== QUIZ FUNNEL EDITOR ====================
export const QuizFunnelEditor = ({ config, onChange }) => {
  const quiz = config.quiz || {};

  const updateQuiz = (key, value) => onChange({ ...config, quiz: { ...quiz, [key]: value } });

  const updateQuestion = (index, key, value) => {
    const questions = [...(quiz.questions || [])];
    questions[index] = { ...questions[index], [key]: value };
    updateQuiz('questions', questions);
  };

  const updateQuestionOption = (qIdx, oIdx, value) => {
    const questions = [...(quiz.questions || [])];
    const options = [...(questions[qIdx].options || [])];
    options[oIdx] = value;
    questions[qIdx] = { ...questions[qIdx], options };
    updateQuiz('questions', questions);
  };

  const addQuestion = () => {
    const questions = [...(quiz.questions || [])];
    questions.push({ label: `Question ${questions.length + 1}`, options: ['Option A', 'Option B'], layout: 'vertical' });
    updateQuiz('questions', questions);
  };

  const removeQuestion = (index) => {
    updateQuiz('questions', (quiz.questions || []).filter((_, i) => i !== index));
  };

  const addOption = (qIdx) => {
    const questions = [...(quiz.questions || [])];
    const options = [...(questions[qIdx].options || [])];
    options.push(`Option ${options.length + 1}`);
    questions[qIdx] = { ...questions[qIdx], options };
    updateQuiz('questions', questions);
  };

  const removeOption = (qIdx, oIdx) => {
    const questions = [...(quiz.questions || [])];
    const options = (questions[qIdx].options || []).filter((_, i) => i !== oIdx);
    questions[qIdx] = { ...questions[qIdx], options };
    updateQuiz('questions', questions);
  };

  return (
    <div className="space-y-6 p-4 overflow-y-auto h-full">

      {/* Appearance */}
      <div className="space-y-3">
        <h4 className="font-semibold text-sm flex items-center gap-2"><Palette className="h-4 w-4" /> Appearance</h4>
        <ColorPicker label="Page Background" value={quiz.backgroundColor} onChange={v => updateQuiz('backgroundColor', v)} placeholder="#7cb342" />
        <ColorPicker label="Container Background" value={quiz.containerBgColor} onChange={v => updateQuiz('containerBgColor', v)} placeholder="#ffffff" />
        <Input label="Logo URL" value={quiz.logoUrl || ''} onChange={e => updateQuiz('logoUrl', e.target.value)} placeholder="https://..." />
      </div>

      <hr />

      {/* Content */}
      <div className="space-y-3">
        <h4 className="font-semibold text-sm flex items-center gap-2"><Type className="h-4 w-4" /> Content</h4>
        <Input label="Title" value={quiz.title || ''} onChange={e => updateQuiz('title', e.target.value)} placeholder="Answer to Continue" />
        <div className="grid grid-cols-2 gap-2">
          <Input label="Title Size" value={quiz.titleSize || '20px'} onChange={e => updateQuiz('titleSize', e.target.value)} placeholder="20px" />
          <ColorPicker label="Color" value={quiz.titleColor} onChange={v => updateQuiz('titleColor', v)} />
        </div>
        <Input label="Subtitle (optional)" value={quiz.subtitle || ''} onChange={e => updateQuiz('subtitle', e.target.value)} placeholder="Please answer all questions below" />
        <ColorPicker label="Subtitle Color" value={quiz.subtitleColor} onChange={v => updateQuiz('subtitleColor', v)} />
      </div>

      <hr />

      {/* Questions */}
      <div className="space-y-3">
        <h4 className="font-semibold text-sm flex items-center gap-2"><HelpCircle className="h-4 w-4" /> Questions</h4>
        <ColorPicker label="Question Text Color" value={quiz.questionColor} onChange={v => updateQuiz('questionColor', v)} />
        <ColorPicker label="Option Text Color" value={quiz.optionTextColor} onChange={v => updateQuiz('optionTextColor', v)} />

        {(quiz.questions || []).map((q, qIdx) => (
          <div key={qIdx} className="border rounded-lg p-3 space-y-3 bg-muted/40">
            <div className="flex gap-2 items-start">
              <div className="flex-1">
                <Input
                  label=""
                  value={q.label || ''}
                  onChange={e => updateQuestion(qIdx, 'label', e.target.value)}
                  placeholder={`Question ${qIdx + 1} label (leave blank to hide)`}
                />
              </div>
              <button onClick={() => removeQuestion(qIdx)} className="p-2 text-red-500 hover:bg-red-50 rounded mt-0.5">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            {/* Layout toggle */}
            <div>
              <label className="block text-xs font-medium mb-1">Option Layout</label>
              <div className="flex gap-2">
                <button
                  onClick={() => updateQuestion(qIdx, 'layout', 'vertical')}
                  className={`flex-1 py-1 text-xs border rounded ${q.layout !== 'horizontal' ? 'bg-primary text-white border-primary' : ''}`}
                >Vertical</button>
                <button
                  onClick={() => updateQuestion(qIdx, 'layout', 'horizontal')}
                  className={`flex-1 py-1 text-xs border rounded ${q.layout === 'horizontal' ? 'bg-primary text-white border-primary' : ''}`}
                >Horizontal (2 cols)</button>
              </div>
            </div>

            {/* Options */}
            <div className="space-y-2">
              <label className="block text-xs font-medium">Options</label>
              {(q.options || []).map((opt, oIdx) => (
                <div key={oIdx} className="flex gap-2">
                  <input
                    value={opt}
                    onChange={e => updateQuestionOption(qIdx, oIdx, e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border border-border rounded bg-card text-foreground"
                    placeholder={`Option ${oIdx + 1}`}
                  />
                  {(q.options || []).length > 1 && (
                    <button onClick={() => removeOption(qIdx, oIdx)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => addOption(qIdx)} className="w-full">
                <Plus className="h-3 w-3 mr-1" /> Add Option
              </Button>
            </div>
          </div>
        ))}

        <Button size="sm" variant="outline" onClick={addQuestion} className="w-full">
          <Plus className="h-3 w-3 mr-1" /> Add Question
        </Button>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={quiz.showProgress || false} onChange={e => updateQuiz('showProgress', e.target.checked)} className="rounded" />
          Show progress bar
        </label>
      </div>

      <hr />

      {/* Final CTA */}
      <div className="space-y-3">
        <h4 className="font-semibold text-sm flex items-center gap-2"><MousePointerClick className="h-4 w-4" /> Final CTA</h4>
        <Input label="CTA Button Text" value={quiz.ctaText || ''} onChange={e => updateQuiz('ctaText', e.target.value)} placeholder="👉 Continue" />
        <Input label="Destination URL" value={quiz.ctaDestinationUrl || ''} onChange={e => updateQuiz('ctaDestinationUrl', e.target.value)} placeholder="https://..." />
        <ColorPicker label="CTA Text Color" value={quiz.ctaTextColor} onChange={v => updateQuiz('ctaTextColor', v)} placeholder="#ffffff" />
        <Input label="Locked Message" value={quiz.ctaLockedMessage || ''} onChange={e => updateQuiz('ctaLockedMessage', e.target.value)} placeholder="Answer all questions to continue" />
        <label className="flex items-center gap-2 p-3 border rounded-lg bg-purple-50">
          <input type="checkbox" checked={quiz.isTestCta || false} onChange={e => updateQuiz('isTestCta', e.target.checked)} className="rounded" />
          <div>
            <span className="text-sm font-medium">Enable for A/B Testing</span>
            <p className="text-xs text-muted-foreground">Assign this CTA to a test in Projects</p>
          </div>
        </label>
      </div>

      <hr />

      {/* Footer */}
      <div className="space-y-3">
        <h4 className="font-semibold text-sm">Footer</h4>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={quiz.showTermsLink || false} onChange={e => updateQuiz('showTermsLink', e.target.checked)} className="rounded" />
          <span className="text-sm">Show Terms & Conditions link</span>
        </label>
        {quiz.showTermsLink && (
          <>
            <Input label="Footer text (before link)" value={quiz.termsText || ''} onChange={e => updateQuiz('termsText', e.target.value)} placeholder="Subject to our" />
            <Input label="Link text" value={quiz.termsLinkText || ''} onChange={e => updateQuiz('termsLinkText', e.target.value)} placeholder="T&Cs" />
            <Input label="Terms URL" value={quiz.termsUrl || ''} onChange={e => updateQuiz('termsUrl', e.target.value)} placeholder="https://..." />
            <ColorPicker label="Text & Link Color" value={quiz.termsColor} onChange={v => updateQuiz('termsColor', v)} />
          </>
        )}
      </div>
    </div>
  );
};
